import { useQuery } from 'react-query';
import { LocationData } from '@/components/organisms/search-bar/search-bar.types';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

interface UseQueryCampaignsDestinationHierarchyData {
  departures: LocationData['transportationOptions'];
  destinations: LocationData['locations'];
}

interface UseQueryCampaignsDestinationHierarchyOptions {
  initialData?: UseQueryCampaignsDestinationHierarchyData;
}

interface UseQueryCampaignsDestinationHierarchyParameters {
  area: string;
  campaignId: number | null;
  campaignType: string;
}

const useQueryCampaignsDestinationHierarchy = (
  parameters: UseQueryCampaignsDestinationHierarchyParameters,
  options?: UseQueryCampaignsDestinationHierarchyOptions,
) => {
  const locale = getLocale();
  const query = useQuery<UseQueryCampaignsDestinationHierarchyData>(
    [QueryId.CAMPAIGNS_DESTINATION_HIERARCHY, parameters, locale],
    async () => {
      const baseUrl = '/api/campaigns/destination-hierarchy';
      const searchParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryCampaignsDestinationHierarchy', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!parameters.area && !!parameters.campaignType,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryCampaignsDestinationHierarchy;
